import type { CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { Kicker, Pill } from "@/components/ui/v2";
import { getPlatform } from "@/lib/platform";
import { YouFrame } from "@/pages/you/YouFrame";
import {
  GUTTER,
  LEAD,
  MONO_TAG,
  TITLE_DIM,
  TITLE_SM,
} from "@/pages/you/typography";

/**
 * The failure screen — 04-YOU.md §Error, composed as `ErrorScreen` in
 * `docs/design/handoff/design/templates/you/you-screen.jsx`:
 * `padding-top: 120`, kicker `ERROR`, a two-clause headline whose second
 * half is `--d-fg-3`, one paragraph, a mono reason line, then the way back
 * 34 below.
 *
 * **The error is copy, not a red.** The only sanctioned red in the product
 * is the account-deletion pair.
 *
 * The prototype's reason line lists three possibilities to show the slot;
 * in production it is one — and this screen is the generic boundary, which
 * knows only that the request failed.
 *
 * Deliberately renders no tab bar and reaches for no router hook: the app's
 * error boundary can render this when the router itself is what threw.
 */

const BLOCK: CSSProperties = {
  ...GUTTER,
  paddingTop: 120,
  display: "flex",
  flexDirection: "column",
  gap: 12,
};

const DETAIL: CSSProperties = {
  font: "400 10px/16px var(--font-mono)",
  color: "var(--d-fg-4)",
  margin: 0,
  maxWidth: "100%",
  overflowX: "auto",
  whiteSpace: "pre-wrap",
};

const SUPPORT: CSSProperties = {
  font: "600 14px/20px var(--font-body)",
  color: "var(--d-fg-2)",
  textDecoration: "none",
};

interface ErrorPageProps {
  error?: Error;
  resetError?: () => void;
}

export function ErrorPage({ error, resetError }: ErrorPageProps = {}): JSX.Element {
  const { t } = useTranslation("you");

  const handleRestart = (): void => {
    getPlatform().haptics.impact("light");
    if (resetError) {
      resetError();
      return;
    }
    try {
      getPlatform().close();
    } catch {
      window.location.reload();
    }
  };

  return (
    /* 04-YOU.md `NO_TABBAR`: `error` is one of the four screens that go
     * without the bar. It is also why the frame must not reach for a router
     * here — the boundary can be rendering this because the router itself is
     * what failed. */
    <YouFrame bar={false}>
      <div style={BLOCK}>
        <Kicker>{t("error.kicker")}</Kicker>
        <h2 style={TITLE_SM}>
          {t("error.title_1")} <span style={TITLE_DIM}>{t("error.title_2")}</span>
        </h2>
        <p style={LEAD}>{t("error.body")}</p>
        <span style={{ ...MONO_TAG, lineHeight: "16px" }} data-testid="error-reason">
          {t("error.reason")}
        </span>
        {error?.message && import.meta.env.DEV ? (
          <pre style={DETAIL} data-testid="error-detail">
            {error.message}
          </pre>
        ) : null}
      </div>

      <div
        style={{
          ...GUTTER,
          marginTop: 34,
          display: "flex",
          flexDirection: "column",
          gap: 18,
          alignItems: "stretch",
        }}
      >
        <Pill label={t("error.cta")} onClick={handleRestart} data-testid="error-retry" />
        <span style={{ display: "flex", justifyContent: "center" }}>
          <a
            href="https://t.me/mavk_support"
            target="_blank"
            rel="noreferrer noopener"
            style={SUPPORT}
            data-testid="error-support"
          >
            {t("error.support")}
          </a>
        </span>
      </div>
    </YouFrame>
  );
}

export default ErrorPage;
